import { createFileRoute, Link } from '@tanstack/react-router';

import { SettingsLoadingState } from '@apps/web/components/settings/settings_page_shell';
import { TeamNotificationDefaults } from '@apps/web/components/settings/team_notification_defaults';
import {
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import {
    isNotificationChannelSlug,
    type NotificationChannelSlug,
} from '@apps/web/lib/notification_channel_pages';
import { isOrganizationAdmin } from '@apps/web/lib/organization_permissions';
import { useUserProfile } from '@apps/web/swr_data_fetching/current_user_queries';

export const Route = createFileRoute('/_app/settings/team-members_/defaults')({
    validateSearch: (
        search: Record<string, unknown>,
    ): { channel?: NotificationChannelSlug } => ({
        channel:
            typeof search['channel'] === 'string' &&
            isNotificationChannelSlug(search['channel'])
                ? search['channel']
                : undefined,
    }),
    component: TeamNotificationDefaultsPage,
});

function TeamNotificationDefaultsPage() {
    const { channel } = Route.useSearch();
    const { organizationId, isLoading } = useSettingsGates();
    const { data: profile, isLoading: isLoadingProfile } = useUserProfile({
        reason: 'team-notification-defaults-access',
    });
    if (isLoading || isLoadingProfile) {
        return <SettingsLoadingState />;
    }
    const canManage = isOrganizationAdmin(profile, organizationId);
    return (
        <SettingsPageShell
            title="Team notification defaults"
            description="Set the starting preferences for your team. Review changes before saving and choose how to handle teammates with different settings."
            eyebrow={
                <Link
                    to="/settings/team-members"
                    className="text-muted-foreground hover:text-foreground text-sm pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                >
                    ← Team Members
                </Link>
            }
        >
            {canManage && organizationId ? (
                <TeamNotificationDefaults
                    key={organizationId}
                    organizationId={organizationId}
                    channel={channel}
                />
            ) : (
                <p role="alert">
                    Only organization admins can manage team defaults.
                </p>
            )}
        </SettingsPageShell>
    );
}
